import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminAssignInternalUserRole,
  AdminCreateInternalRole,
  AdminFetchInternalRole,
  AdminFetchInternalRoles,
  AdminFetchInternalUsers,
  AdminInviteInternalUser,
  AdminSetInternalUserStatus,
  AdminUpdateInternalRole,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  AssignInternalUserRolePayload,
  CreateInternalRolePayload,
  FetchInternalRolePayload,
  InternalAccessRole,
  InternalAccessUser,
  InternalRoleUpdateBody,
  InviteInternalUserPayload,
  SetInternalUserStatusPayload,
  UpdateInternalRolePayload,
} from 'src/interfaces/internalAccess'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

// The handlers take `Partial` payloads and guard ids at runtime: an action can be dispatched with
// a malformed payload from anywhere. Same split as `onUpdateRetryConfigTemplate`.

export function* onFetchInternalUsers(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInternalUsers,
      { method: 'GET' },
    )

    const users = normalizeApiListPayload<InternalAccessUser>(payload)

    yield put(AdminFetchInternalUsers.success(users))

    if (action.next) {
      action.next(null, users)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onFetchInternalUsers] error' })

    yield put(AdminFetchInternalUsers.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load users'))
    }
  }
}

export function* onInviteInternalUser(action: AsyncAction<Partial<InviteInternalUserPayload>>) {
  const email = action.payload?.email
  const roleId = action.payload?.roleId

  if (!email || !roleId) {
    yield put(AdminInviteInternalUser.failed(new Error('Missing email or roleId')))

    if (action.next) {
      action.next('Missing email or roleId')
    }

    return
  }

  try {
    const { payload }: { payload: InternalAccessUser } = yield call(
      callSecureApi,
      Endpoints.AdminInviteInternalUser,
      {
        method: 'POST',
        body: { email, roleId },
      },
    )

    yield put(AdminInviteInternalUser.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onInviteInternalUser] error' })

    yield put(AdminInviteInternalUser.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to invite the user'))
    }
  }
}

export function* onAssignInternalUserRole(
  action: AsyncAction<Partial<AssignInternalUserRolePayload>>,
) {
  const userId = action.payload?.userId
  const roleId = action.payload?.roleId

  if (!userId || !roleId) {
    yield put(AdminAssignInternalUserRole.failed(new Error('Missing userId or roleId')))

    if (action.next) {
      action.next('Missing userId or roleId')
    }

    return
  }

  try {
    const { payload }: { payload: InternalAccessUser } = yield call(
      callSecureApi,
      Endpoints.AdminAssignInternalUserRole,
      {
        method: 'PATCH',
        params: [{ field: ':userId', value: userId }],
        body: { roleId },
      },
    )

    yield put(AdminAssignInternalUserRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onAssignInternalUserRole] error' })

    yield put(AdminAssignInternalUserRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to change the role'))
    }
  }
}

export function* onSetInternalUserStatus(
  action: AsyncAction<Partial<SetInternalUserStatusPayload>>,
) {
  const userId = action.payload?.userId
  const disabled = action.payload?.disabled

  if (!userId || typeof disabled !== 'boolean') {
    yield put(AdminSetInternalUserStatus.failed(new Error('Missing userId or disabled')))

    if (action.next) {
      action.next('Missing userId or disabled')
    }

    return
  }

  try {
    const { payload }: { payload: InternalAccessUser } = yield call(
      callSecureApi,
      Endpoints.AdminSetInternalUserStatus,
      {
        method: 'PATCH',
        params: [{ field: ':userId', value: userId }],
        body: { disabled },
      },
    )

    yield put(AdminSetInternalUserStatus.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onSetInternalUserStatus] error' })

    yield put(AdminSetInternalUserStatus.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to change the user status'))
    }
  }
}

export function* onFetchInternalRoles(action: AsyncAction<undefined>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInternalRoles,
      { method: 'GET' },
    )

    const roles = normalizeApiListPayload<InternalAccessRole>(payload)

    yield put(AdminFetchInternalRoles.success(roles))

    if (action.next) {
      action.next(null, roles)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onFetchInternalRoles] error' })

    yield put(AdminFetchInternalRoles.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load roles'))
    }
  }
}

export function* onFetchInternalRole(action: AsyncAction<Partial<FetchInternalRolePayload>>) {
  const roleId = action.payload?.roleId

  if (!roleId) {
    yield put(AdminFetchInternalRole.failed(new Error('Missing roleId')))

    if (action.next) {
      action.next('Missing roleId')
    }

    return
  }

  try {
    const { payload }: { payload: InternalAccessRole } = yield call(
      callSecureApi,
      Endpoints.AdminFetchInternalRole,
      {
        method: 'GET',
        params: [{ field: ':roleId', value: roleId }],
      },
    )

    yield put(AdminFetchInternalRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onFetchInternalRole] error' })

    yield put(AdminFetchInternalRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load the role'))
    }
  }
}

export function* onCreateInternalRole(action: AsyncAction<Partial<CreateInternalRolePayload>>) {
  const name = action.payload?.name
  const permissions = action.payload?.permissions

  if (!name || !Array.isArray(permissions)) {
    yield put(AdminCreateInternalRole.failed(new Error('Missing name or permissions')))

    if (action.next) {
      action.next('Missing name or permissions')
    }

    return
  }

  try {
    const { payload }: { payload: InternalAccessRole } = yield call(
      callSecureApi,
      Endpoints.AdminCreateInternalRole,
      {
        method: 'POST',
        body: { name, permissions },
      },
    )

    yield put(AdminCreateInternalRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onCreateInternalRole] error' })

    yield put(AdminCreateInternalRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create the role'))
    }
  }
}

export function* onUpdateInternalRole(action: AsyncAction<Partial<UpdateInternalRolePayload>>) {
  const roleId = action.payload?.roleId

  if (!roleId) {
    yield put(AdminUpdateInternalRole.failed(new Error('Missing roleId')))

    if (action.next) {
      action.next('Missing roleId')
    }

    return
  }

  const { name, permissions } = action.payload
  // Only the keys the editor changed: the backend re-checks every permission in a submitted list
  // against the editor's own and refuses an empty body.
  const body: InternalRoleUpdateBody = {}

  if (name !== undefined) {
    body.name = name
  }

  if (permissions !== undefined) {
    body.permissions = permissions
  }

  try {
    const { payload }: { payload: InternalAccessRole } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateInternalRole,
      {
        method: 'PATCH',
        params: [{ field: ':roleId', value: roleId }],
        body,
      },
    )

    yield put(AdminUpdateInternalRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.internalAccess.onUpdateInternalRole] error' })

    yield put(AdminUpdateInternalRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the role'))
    }
  }
}

export function* internalAccessSaga() {
  yield takeLatest(AdminFetchInternalUsers.type.REQUEST, onFetchInternalUsers)
  yield takeLatest(AdminFetchInternalRoles.type.REQUEST, onFetchInternalRoles)
  yield takeLatest(AdminFetchInternalRole.type.REQUEST, onFetchInternalRole)
  yield takeEvery(AdminInviteInternalUser.type.REQUEST, onInviteInternalUser)
  yield takeEvery(AdminAssignInternalUserRole.type.REQUEST, onAssignInternalUserRole)
  yield takeEvery(AdminSetInternalUserStatus.type.REQUEST, onSetInternalUserStatus)
  yield takeEvery(AdminCreateInternalRole.type.REQUEST, onCreateInternalRole)
  yield takeEvery(AdminUpdateInternalRole.type.REQUEST, onUpdateInternalRole)
}
